import { useNavigate, useParams } from "react-router-dom";
import { formatBytes } from "../../../../../utils/numbers";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { graphql } from "gql.tada";
import {
  ActionIcon,
  Badge,
  Button,
  Chip,
  Group,
  Loader,
  Menu,
  Paper,
  rem,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { CreationForm } from "../../../components/form/CreationForm";
import * as yup from "yup";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { PlatformCdnFileType } from "../../../../../gql/graphql";
import { CDN_BASE_URL } from "../../../../../utils/cdn";
import {
  IconFile,
  IconFileText,
  IconPhoto,
  IconPlus,
  IconSearch,
  IconWorld,
  IconDots,
  IconTrash,
  IconExternalLink,
  IconCopy,
  IconPuzzle,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";

export const PlatformFileQuery = graphql(`
  query platformFile($id: ID!) {
    platformFile(id: $id) {
      id
      name
      platformId
      type
      prodCdnPath
      contents
      devCdnPath
      config {
        pageConfig {
          path
        }
      }
    }
  }
`);

const PlatformFileCreateMutation = graphql(`
  mutation platformFileCreate(
    $platformId: ID!
    $input: PlatformCdnFileCreateInput!
  ) {
    platformFileCreate(platformId: $platformId, input: $input) {
      id
    }
  }
`);

const PlatformFileUpdateMutation = graphql(`
  mutation platformFileUpdateData($id: ID!, $input: PlatformFileUpdateInput!) {
    platformFileUpdate(id: $id, input: $input) {
      id
    }
  }
`);

const PLATFORM_FILES_QUERY = gql`
  query platformFilesAll($platformId: ID!) {
    platformFiles(platformId: $platformId) {
      id
      name
      type
      devCdnPath
      prodCdnPath
      size
      updatedAt
      config {
        pageConfig {
          path
        }
      }
    }
  }
`;

const PLATFORM_FILE_DELETE_MUTATION = gql`
  mutation platformFileDelete($id: ID!) {
    platformFileDelete(id: $id)
  }
`;

const FILE_TYPE_CONFIG: Record<
  string,
  { icon: typeof IconFile; color: string; label: string }
> = {
  PAGE: { icon: IconWorld, color: "cyan", label: "Page" },
  COMPONENT: { icon: IconPuzzle, color: "purple", label: "Component" },
  IMAGE: { icon: IconPhoto, color: "green", label: "Image" },
  TEXT: { icon: IconFileText, color: "amber", label: "Text" },
};

const useStyles = createStyles((theme) => ({
  fileCard: {
    cursor: "pointer",
    transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
    border: `1px solid ${theme.colors.dark[5]}`,
    "&:hover": {
      borderColor: theme.colors.cyan[7],
      transform: "translateY(-2px)",
      boxShadow: theme.shadows.md,
    },
  },
  fileIcon: {
    width: rem(44),
    height: rem(44),
    borderRadius: theme.radius.md,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  fileName: {
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[0],
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileMeta: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[2],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  toolbar: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    paddingBottom: theme.spacing.md,
  },
  chipGroup: {
    flexWrap: "wrap",
  },
  publishedDot: {
    width: rem(6),
    height: rem(6),
    borderRadius: "50%",
    backgroundColor: theme.colors.green[5],
    flexShrink: 0,
  },
  unpublishedDot: {
    width: rem(6),
    height: rem(6),
    borderRadius: "50%",
    backgroundColor: theme.colors.dark[4],
    flexShrink: 0,
  },
  cardFooter: {
    borderTop: `1px solid ${theme.colors.dark[5]}`,
  },
}));

interface PlatformFile {
  id: string;
  name: string;
  type: string;
  devCdnPath: string | null;
  prodCdnPath: string | null;
  size: number;
  updatedAt: string;
  config: {
    pageConfig: {
      path: string;
    } | null;
  } | null;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export const PlatformFileUpdate = () => {
  const navigate = useNavigate();
  const platformId = useParams().platformId as string;
  let fileId = useParams().fileId as string;
  const [fileType, setFileType] = useState("PAGE" as const);
  const { data, loading } = useQuery<any>(PlatformFileQuery, {
    variables: {
      id: fileId,
    },
    skip: !fileId,
  });
  const [fileUpdate] = useMutation<any>(PlatformFileUpdateMutation);
  const [fileCreate] = useMutation<any>(PlatformFileCreateMutation);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {!fileId && (
        <Select
          label={"File type"}
          data={Object.values(PlatformCdnFileType).map((v) => ({
            label: v,
            value: v,
          }))}
          value={fileType}
          onChange={(value) => setFileType(value as any)}
        />
      )}
      <CreationForm
        title={fileId ? "Update file" : "Create file"}
        schemaDefinition={{
          ...(!fileId && {
            name: {
              type: "INPUT",
              label: "Name",
              yupConfig: yup.string().required(),
              inputType: "text",
              defaultValue: data?.platformFile?.name,
              placeholder: "Enter the file name",
            },
          }),
          ...(fileType === "PAGE" && {
            path: {
              type: "INPUT",
              label: "Path",
              info: (
                <>
                  The path of the page, must start with /,
                  <br />
                  and can contain alphanumeric characters and : for parameters
                  <br />
                  Example: /users/:userId
                </>
              ),
              yupConfig: yup
                .string()
                .matches(
                  /^\/(?:[a-zA-Z0-9]*|:[a-zA-Z0-9]+)*(?:\/(?:[a-zA-Z0-9]*|:[a-zA-Z0-9]+)*)*$/,
                )
                .required(),
              inputType: "text",
              defaultValue: data?.platformFile?.config?.pageConfig?.path,
              placeholder: "Enter the file path",
            },
          }),
        }}
        onSubmit={async (values) => {
          if (fileId) {
            await fileUpdate({
              variables: {
                id: fileId,
                input:
                  (values.path && {
                    config: { pageConfig: { path: values.path } },
                  }) ??
                  {},
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
            });
            notifications.show({
              title: "File updated",
              message: "The file has been updated successfully",
              color: "green",
              autoClose: 3000,
            });
          } else {
            fileId = await fileCreate({
              variables: {
                platformId,
                input: {
                  name: values.name,
                  config: values.path
                    ? { pageConfig: { path: values.path } }
                    : {},
                  type: fileType,
                },
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
              return data.data?.platformFileCreate.id as string;
            });
            navigate(("../files/" + fileId) as string);
          }
        }}
      />
    </>
  );
};

export const PlatformFiles = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const platformId = useParams().platformId;
  const [search, setSearch] = useState("");
  const [activeTypes, setActiveTypes] = useState<string[]>([]);

  const { data, loading, refetch } = useQuery<any>(PLATFORM_FILES_QUERY, {
    variables: { platformId },
  });

  const [deleteFile] = useMutation<any>(PLATFORM_FILE_DELETE_MUTATION);

  const files: PlatformFile[] = data?.platformFiles ?? [];

  const filteredFiles = useMemo(() => {
    let result = files;
    if (activeTypes.length > 0) {
      result = result.filter((f) => activeTypes.includes(f.type));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          f.config?.pageConfig?.path?.toLowerCase().includes(q),
      );
    }
    return result;
  }, [files, activeTypes, search]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const f of files) {
      counts[f.type] = (counts[f.type] ?? 0) + 1;
    }
    return counts;
  }, [files]);

  const handleDelete = async (file: PlatformFile) => {
    const confirmed = await new Promise<boolean>((resolve) =>
      modals.openConfirmModal({
        title: "Delete file",
        children: (
          <Text size="sm">
            Are you sure you want to delete <strong>{file.name}</strong>? This
            action cannot be undone.
          </Text>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      }),
    );
    if (!confirmed) return;

    await deleteFile({ variables: { id: file.id } });
    notifications.show({
      title: "File deleted",
      message: `${file.name} has been deleted`,
      color: "green",
      autoClose: 3000,
    });
    refetch();
  };

  const copyCdnUrl = (url: string) => {
    const fullUrl = `${CDN_BASE_URL}/${url}`;
    navigator.clipboard.writeText(fullUrl);
    notifications.show({
      title: "Copied",
      message: "CDN URL copied to clipboard",
      autoClose: 2000,
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Stack gap="md">
      <div className={classes.toolbar}>
        <Stack gap="sm">
          <Group justify="space-between">
            <TextInput
              placeholder="Search files..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: rem(360) }}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate("new")}
            >
              New file
            </Button>
          </Group>

          <Chip.Group multiple value={activeTypes} onChange={setActiveTypes}>
            <Group gap="xs" className={classes.chipGroup}>
              {Object.entries(FILE_TYPE_CONFIG).map(([type, config]) => {
                const count = typeCounts[type] ?? 0;
                return (
                  <Chip
                    key={type}
                    value={type}
                    variant="filled"
                    color={config.color}
                    size="xs"
                  >
                    {config.label} ({count})
                  </Chip>
                );
              })}
            </Group>
          </Chip.Group>
        </Stack>
      </div>

      {filteredFiles.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center" gap="sm">
            <ThemeIcon size={48} radius="xl" variant="light" color="dark">
              <IconFile size={24} />
            </ThemeIcon>
            <Text c="dimmed" size="sm">
              {files.length === 0
                ? "No files yet. Create your first file to get started."
                : "No files match your search."}
            </Text>
          </Stack>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 3, lg: 3 }} spacing="md">
          {filteredFiles.map((file) => {
            const typeConfig = FILE_TYPE_CONFIG[file.type] ?? {
              icon: IconFile,
              color: "dark",
              label: file.type,
            };
            const FileIcon = typeConfig.icon;
            const isPublished = !!file.prodCdnPath;

            return (
              <Paper
                key={file.id}
                p="md"
                className={classes.fileCard}
                onClick={() => navigate(file.id)}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Group gap="sm" wrap="nowrap" style={{ overflow: "hidden" }}>
                    <ThemeIcon
                      size={44}
                      radius="md"
                      variant="light"
                      color={typeConfig.color}
                      className={classes.fileIcon}
                    >
                      <FileIcon size={22} />
                    </ThemeIcon>
                    <Stack gap={2} style={{ overflow: "hidden" }}>
                      <Text className={classes.fileName}>{file.name}</Text>
                      <Group gap={6} wrap="nowrap">
                        <Badge size="xs" variant="dot" color={typeConfig.color}>
                          {typeConfig.label}
                        </Badge>
                        {file.config?.pageConfig?.path && (
                          <Text className={classes.fileMeta}>
                            {file.config.pageConfig.path}
                          </Text>
                        )}
                      </Group>
                    </Stack>
                  </Group>

                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconDots size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {file.devCdnPath && (
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyCdnUrl(file.devCdnPath as string);
                          }}
                        >
                          Copy dev CDN URL
                        </Menu.Item>
                      )}
                      {file.prodCdnPath && (
                        <Menu.Item
                          leftSection={<IconCopy size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyCdnUrl(file.prodCdnPath as string);
                          }}
                        >
                          Copy prod CDN URL
                        </Menu.Item>
                      )}
                      {file.devCdnPath && (
                        <Menu.Item
                          leftSection={<IconExternalLink size={14} />}
                          component="a"
                          href={`${CDN_BASE_URL}/${file.devCdnPath}`}
                          target="_blank"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          Open in CDN
                        </Menu.Item>
                      )}
                      <Menu.Divider />
                      <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(file);
                        }}
                      >
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>

                <Group
                  justify="space-between"
                  mt="sm"
                  gap="xs"
                  wrap="nowrap"
                  className={classes.cardFooter}
                  pt="xs"
                >
                  <Group gap={6} wrap="nowrap">
                    <Tooltip
                      label={isPublished ? "Published" : "Not published"}
                    >
                      <div
                        className={
                          isPublished
                            ? classes.publishedDot
                            : classes.unpublishedDot
                        }
                      />
                    </Tooltip>
                    <Text className={classes.fileMeta}>
                      {isPublished ? "Published" : "Draft"}
                    </Text>
                  </Group>
                  <Group gap={8} wrap="nowrap">
                    {file.size > 0 && (
                      <Text className={classes.fileMeta}>
                        {formatBytes(file.size)}
                      </Text>
                    )}
                    <Text className={classes.fileMeta}>
                      {formatRelativeTime(file.updatedAt)}
                    </Text>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
};
